import { isBefore } from 'date-fns';
import { useFormikContext } from 'formik';
import {
  FC,
  InputHTMLAttributes,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Box } from 'src/components/box';
import { DropdownContent } from 'src/components/buttons-and-actions/icon-dropdown';
import {
  useIsInitiallyAfter,
  useIsInitiallyBefore,
} from 'src/components/form/datepicker/hooks';
import {
  DateInputField,
  ErrorWrapper,
} from 'src/components/form/datepicker/input-field';
import {
  CalendarContainer,
  RangeCalendar,
} from 'src/components/form/datepicker/range-calendar';
import {
  Hour,
  InputTime,
  transformValueToInputMask,
  validateDateInput,
} from 'src/components/form/datepicker/utils';
import { ErrorMessage } from 'src/components/form/error-message';
import { FieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { InputContainer } from 'src/components/form/inner-addon';
import { Label } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { Stack } from 'src/components/layout/stack';
import { Hint } from 'src/components/text/hint';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useDropdown } from 'src/hooks/use-dropdown';
import { useId } from 'src/hooks/use-id';

type CalendarRangeInputProps = {
  fromName: string;
  fromLabel?: string;
  toName: string;
  toLabel?: string;
  time?: InputTime;
  defaultHour?: Hour;
  fromInputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'value'>;
  toInputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'value'>;
  getFromCustomErrorMessage?: (message: string) => string | undefined;
  getToCustomErrorMessage?: (message: string) => string | undefined;
  stackedGroup?: boolean;
  stackedLabel?: boolean;
  minBookingDate?: Date;
  maxBookingDate?: Date;
  groupLabel?: string;
  hideLabel?: boolean;
  interval?: (fromDate: Date) => Date;
  fromHint?: ReactElement | string;
  toHint?: ReactElement | string;
  info?: ReactElement | string;
  /**
   * Appends an asterisk (*) to `fromLabel`.
   *
   * Should be set to true when this field is required, but there are
   * other fields in the containing form that are not required.
   *
   * If all fields in a form are required, the convention is to omit the
   * required marker.
   *
   * If you use this, you need to *manually* handle the asterisk for `groupLabel`!
   */
  fromMarkAsRequired?: boolean;
  /**
   * Appends an asterisk (*) to `toLabel`.
   *
   * Should be set to true when this field is required, but there are
   * other fields in the containing form that are not required.
   *
   * If all fields in a form are required, the convention is to omit the
   * required marker.
   *
   * If you use this, you need to *manually* handle the asterisk for `groupLabel`!
   */
  toMarkAsRequired?: boolean;
};

export const DateRangeInput: FC<CalendarRangeInputProps> = (props) => {
  const { minTablet } = useBreakpoints();

  const {
    time = false,
    defaultHour,
    fromName,
    fromLabel = 'From',
    toName,
    toLabel = 'To',
    fromInputProps,
    toInputProps,
    stackedGroup = !minTablet,
    stackedLabel = !minTablet,
    minBookingDate,
    maxBookingDate,
    groupLabel,
    hideLabel,
    getFromCustomErrorMessage,
    getToCustomErrorMessage,
    interval,
    fromHint,
    toHint,
    info,
    fromMarkAsRequired,
    toMarkAsRequired,
  } = props;

  const { setFieldValue, setFieldTouched } = useFormikContext<unknown>();
  const validate = (value: string | null) => validateDateInput(time, value);

  const fromId = useId();
  const fromUserValueRef = useRef('');
  const setFromUserValue = (value: string) =>
    (fromUserValueRef.current = value);
  const [fromField, showFromFieldError, fromFieldError] = useCustomField<
    string | null
  >(fromName, fromLabel, {
    validate() {
      return validate(fromUserValueRef.current);
    },
  });

  const toId = useId();
  const toUserValueRef = useRef('');
  const setToUserValue = (value: string) => (toUserValueRef.current = value);

  const [toField, showToFieldError, toFieldError] = useCustomField<
    string | null
  >(toName, toLabel, {
    validate() {
      validate(toUserValueRef.current);
    },
  });

  const { active, setActive, setWrapperElement, ...dropdown } = useDropdown({
    placement: 'bottom-start',
  });

  // Set initial value
  useEffect(() => {
    setFromUserValue(transformValueToInputMask(fromField.value, time));
    setToUserValue(transformValueToInputMask(toField.value, time));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // field can be set from the outside, so we need to sync it with input
  useEffect(() => {
    const hasError = Boolean(validate(fromUserValueRef.current));
    // when there is no formik value, but an error, the user is probably still typing
    if (!fromField.value && hasError) return;
    const newValue = transformValueToInputMask(fromField.value, time);
    if (newValue === fromUserValueRef.current) return;
    setFromUserValue(newValue);
  }, [fromField.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // field can be set from the outside, so we need to sync it with input
  useEffect(() => {
    const hasError = Boolean(validate(toUserValueRef.current));
    // when there is no formik value, but an error, the user is probably still typing
    if (!toField.value && hasError) return;
    const newValue = transformValueToInputMask(toField.value, time);
    if (newValue === toUserValueRef.current) return;
    setToUserValue(newValue);
  }, [toField.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Append interval if given
  useEffect(() => {
    if (!interval) return;
    if (!fromField.value) return;

    setFieldValue(
      toName,
      interval(new Date(fromField.value)).toISOString(),
      true
    );
    setActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromField.value, setFieldValue, toName, setActive]);

  // only disable the field, if the initial value is before min booking date,
  // so the field does not become disabled, if the user accidentally enters a date which is too early,
  // so that the user has the chance to change it
  const fromDisabledBecauseInitiallyAfter = useIsInitiallyBefore(
    fromName,
    minBookingDate
  );

  // Swap dates if to field is before from field
  const checkDateRange = useCallback(() => {
    if (
      toField.value &&
      fromField.value &&
      isBefore(new Date(toField.value), new Date(fromField.value)) &&
      !fromDisabledBecauseInitiallyAfter
    ) {
      setFromUserValue(transformValueToInputMask(toField.value, time));
      setToUserValue(transformValueToInputMask(fromField.value, time));
      setFieldValue(toName, fromField.value, false);
      setFieldValue(fromName, toField.value, false);
    }
  }, [
    toField.value,
    fromField.value,
    toName,
    fromName,
    setFieldValue,
    time,
    fromDisabledBecauseInitiallyAfter,
  ]);

  const fromDisplayedLabel = fromLabel + (fromMarkAsRequired ? '*' : '');
  const fromInput = (
    <FieldItem>
      <DateInputField
        referenceRef={!stackedGroup ? dropdown.refs.setReference : undefined}
        time={time}
        fieldId={fromId}
        name={fromName}
        value={fromUserValueRef.current}
        setUserValue={setFromUserValue}
        showError={showFromFieldError}
        setActive={setActive}
        validate={validate}
        onBlur={(e) => {
          checkDateRange();
          fromField.onBlur(e);
        }}
        aria-label={hideLabel ? fromDisplayedLabel : undefined}
        {...fromInputProps}
        disabled={fromInputProps?.disabled || fromDisabledBecauseInitiallyAfter}
      />

      {fromHint && (
        <Hint
          disabled={props.fromInputProps?.disabled}
          children={fromHint}
          mode="formInput"
        />
      )}

      {showFromFieldError && fromFieldError && (
        <ErrorMessage
          data-testid={`${fromField.name}Error`}
          error={fromFieldError}
          label={fromLabel}
          getCustomErrorMessage={getFromCustomErrorMessage}
        />
      )}
    </FieldItem>
  );

  const fromFieldItem =
    (groupLabel && minTablet) || hideLabel ? (
      fromInput
    ) : (
      <FieldLayout stacked={stackedLabel}>
        <Label htmlFor={fromId} error={showFromFieldError}>
          {fromDisplayedLabel}
        </Label>

        {fromInput}
      </FieldLayout>
    );

  // only disable the field, if the initial value is after max booking date,
  // so the field does not become disabled, if the user accidentally enters a date which is too early,
  // so that the user has the chance to change it
  const toDisabledBecauseInitiallyAfter = useIsInitiallyAfter(
    toName,
    maxBookingDate
  );

  const toDisplayedLabel = toLabel + (toMarkAsRequired ? '*' : '');
  const toInput = (
    <FieldItem>
      <DateInputField
        referenceRef={stackedGroup ? dropdown.refs.setReference : undefined}
        time={time}
        fieldId={toId}
        name={toName}
        value={toUserValueRef.current}
        setUserValue={setToUserValue}
        showError={showToFieldError}
        setActive={setActive}
        validate={validate}
        onBlur={(e) => {
          checkDateRange();
          toField.onBlur(e);
        }}
        onTab={() => setActive(false)}
        aria-label={hideLabel ? toDisplayedLabel : undefined}
        {...toInputProps}
        disabled={
          toInputProps?.disabled ||
          Boolean(interval) ||
          toDisabledBecauseInitiallyAfter
        }
      />

      {toHint && (
        <Hint
          disabled={props.toInputProps?.disabled}
          children={toHint}
          mode="formInput"
        />
      )}

      {showToFieldError && toFieldError && (
        <ErrorMessage
          data-testid={`${toField.name}Error`}
          error={toFieldError}
          label={toLabel}
          getCustomErrorMessage={getToCustomErrorMessage}
        />
      )}
    </FieldItem>
  );

  const toFieldItem =
    (groupLabel && minTablet) || hideLabel ? (
      toInput
    ) : (
      <FieldLayout stacked={stackedLabel}>
        <Label htmlFor={toId} error={showToFieldError}>
          {toDisplayedLabel}
        </Label>

        {toInput}
      </FieldLayout>
    );

  return (
    <div ref={setWrapperElement}>
      <InputContainer>
        {groupLabel && minTablet && !hideLabel ? (
          <FieldGroup
            label={groupLabel}
            stacked={stackedLabel}
            stackedChildren={stackedGroup}
            id={fromId}
            info={info}
          >
            {fromFieldItem}
            {toFieldItem}
          </FieldGroup>
        ) : (
          <Stack
            gap={1}
            alignItems="start"
            flow={stackedGroup ? 'row' : 'column'}
          >
            {fromFieldItem}
            {toFieldItem}
          </Stack>
        )}

        {active && (
          <DropdownContent
            ref={dropdown.refs.setFloating}
            style={dropdown.style}
          >
            <Box>
              <RangeCalendar
                fromDisabled={
                  fromInputProps?.disabled || fromDisabledBecauseInitiallyAfter
                }
                toDisabled={
                  toInputProps?.disabled ||
                  Boolean(interval) ||
                  toDisabledBecauseInitiallyAfter
                }
                time={time}
                defaultHour={defaultHour}
                value={{
                  start: fromField.value ? new Date(fromField.value) : null,
                  end: toField.value ? new Date(toField.value) : null,
                }}
                minBookingDate={minBookingDate}
                maxBookingDate={maxBookingDate}
                onSelect={(date) => {
                  const startDateString =
                    date.start && date.start.toISOString();

                  if (startDateString !== fromField.value) {
                    setFromUserValue(
                      transformValueToInputMask(startDateString, time)
                    );
                    setFieldTouched(fromName, true, false);
                    setFieldValue(fromName, startDateString, true);
                  }

                  const endDateString = date.end && date.end.toISOString();

                  if (endDateString !== toField.value) {
                    setToUserValue(
                      transformValueToInputMask(endDateString, time)
                    );
                    // only set "to" field as touched, if endDateString is not null
                    // (if it is null the user is in the middle of a selection and we should not
                    // show errors)
                    setFieldTouched(toName, endDateString !== null, false);
                    setFieldValue(toName, endDateString, true);
                  }

                  if (date.start && date.end && !time) {
                    setActive(false);
                  }
                }}
              />

              <CalendarContainer>
                {fromHint && (
                  <Hint
                    disabled={props.fromInputProps?.disabled}
                    children={fromHint}
                    mode="formInput"
                  />
                )}

                {toHint && (
                  <Hint
                    disabled={props.toInputProps?.disabled}
                    children={toHint}
                    mode="formInput"
                  />
                )}
              </CalendarContainer>

              {((showFromFieldError && fromFieldError) ||
                (showToFieldError && toFieldError)) && (
                <ErrorWrapper>
                  {showFromFieldError && fromFieldError && (
                    <ErrorMessage
                      error={fromFieldError}
                      label={fromLabel}
                      getCustomErrorMessage={getFromCustomErrorMessage}
                    />
                  )}

                  {showToFieldError && toFieldError && (
                    <ErrorMessage
                      error={toFieldError}
                      label={toLabel}
                      getCustomErrorMessage={getToCustomErrorMessage}
                    />
                  )}
                </ErrorWrapper>
              )}
            </Box>
          </DropdownContent>
        )}
      </InputContainer>
    </div>
  );
};
